import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { PLAN_SLUG_TO_NAME, REGIONS } from "@/data/content";
import { getBusinessHook, BUSINESS_HOOKS } from "@/data/whyThisMatters";
import { useRegion } from "@/hooks/useRegion";
import { isTagalogSelected } from "@/data/tagalogTranslations";

const initial = { name: "", email: "", phone: "", website: "", selected_plan: "", message: "", newsletter: false, website_confirm: "" };

export default function ContactForm({ compact = false }) {
  const [searchParams] = useSearchParams();
  const { region, isPH } = useRegion();
  const tagalog = isPH && isTagalogSelected();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    const planSlug = searchParams.get("plan");
    const conceptSlug = searchParams.get("concept");
    const bizName = conceptSlug && BUSINESS_HOOKS[conceptSlug] ? getBusinessHook(conceptSlug).name : "";
    if (planSlug) {
      const planName = PLAN_SLUG_TO_NAME[planSlug] || planSlug;
      setForm((f) => ({
        ...f,
        selected_plan: planName,
        message:
          f.message ||
          (bizName
            ? `I'm interested in the ${planName}, after seeing the website concept you built for ${bizName}.`
            : `I'm interested in the ${planName}.`),
      }));
    } else if (bizName) {
      setForm((f) => ({
        ...f,
        message: f.message || `I'd like to discuss the website concept you built for ${bizName}.`,
      }));
    }
  }, [searchParams]);

  const update = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Invisible honeypot: bots commonly fill this field; real users never see it.
    if (form.website_confirm) {
      setStatus("success");
      setForm(initial);
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      setError(tagalog ? "Ilagay ang inyong pangalan at email." : "Please provide your name and email.");
      return;
    }
    setStatus("loading");
    try {
      const payload = { ...form };
      delete payload.website_confirm;
      await submitContact({ ...payload, source: REGIONS[region].source });
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      setError(tagalog ? "May naganap na error. Mag-email sa amin sa info@mysitecare.eu." : "Something went wrong. Please email us at info@mysitecare.eu.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/15 flex items-center justify-center mb-4">
          <CheckCircle2 size={30} className="text-[#10B981]" />
        </div>
        <h3 className="text-xl font-bold font-display text-[#0F172A]">{tagalog ? "Natanggap ang mensahe" : "Message received"}</h3>
        <p className="mt-2 text-[#475569]">
          {tagalog
            ? "Salamat sa pakikipag-ugnayan. Susuriin namin kung ano ang mayroon kayo at kailangan ninyo, at sasagutin nang may malinaw na susunod na hakbang."
            : "Thanks for reaching out. We'll review what you have and what you need, and reply with clear next steps."}
        </p>
        <button
          data-testid="contact-send-another"
          onClick={() => setStatus("idle")}
          className="mt-6 rounded-xl border-2 border-[#E2E8F0] px-6 py-2.5 text-sm font-semibold text-[#0F172A] hover:border-[#0F172A] transition-colors"
        >
          {tagalog ? "Magpadala ng isa pang mensahe" : "Send another message"}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-5">
      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-5`}>
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Pangalan *" : "Name *"}</label>
          <input
            id="contact-name"
            autoComplete="name"
            required
            data-testid="contact-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            placeholder={tagalog ? "Inyong pangalan" : "Your name"}
            className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8]"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Email *" : "Email *"}</label>
          <input
            id="contact-email"
            autoComplete="email"
            required
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8]"
          />
        </div>
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-5`}>
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Telepono (opsyonal)" : "Phone (optional)"}</label>
          <input
            id="contact-phone"
            autoComplete="tel"
            data-testid="contact-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+63 ..."
            className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8]"
          />
        </div>
        <div>
          <label htmlFor="contact-website" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Website (opsyonal)" : "Website (optional)"}</label>
          <input
            id="contact-website"
            autoComplete="url"
            data-testid="contact-website"
            type="text"
            value={form.website}
            onChange={update("website")}
            placeholder="https://yourbusiness.com"
            className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8]"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-selected-plan" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Napiling plano (opsyonal)" : "Selected plan (optional)"}</label>
        <input
          id="contact-selected-plan"
          data-testid="contact-selected-plan"
          type="text"
          value={form.selected_plan}
          onChange={update("selected_plan")}
          placeholder={tagalog ? "hal. Responsibility Plan" : "e.g. Responsibility Plan"}
          className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8]"
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-[#0F172A] mb-1.5">{tagalog ? "Mensahe" : "Message"}</label>
        <textarea
          id="contact-message"
          data-testid="contact-message"
          value={form.message}
          onChange={update("message")}
          rows={compact ? 3 : 5}
          placeholder={tagalog ? "Sabihin sa amin ang tungkol sa inyong website at kung ano ang kailangang asikasuhin." : "Tell us about your website and what you need handled."}
          className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent placeholder:text-[#94A3B8] resize-none"
        />
      </div>

      <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="contact-website-confirm">Leave this field empty</label>
        <input id="contact-website-confirm" name="website_confirm" tabIndex="-1" autoComplete="off" value={form.website_confirm} onChange={update("website_confirm")} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          data-testid="contact-newsletter"
          type="checkbox"
          checked={form.newsletter}
          onChange={update("newsletter")}
          className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#10B981] focus:ring-[#10B981]"
        />
        <span className="text-sm text-[#475569]">{tagalog ? "Ipaalam sa akin ang paminsan-minsang service update. Walang spam." : "Keep me updated with occasional service updates. No spam."}</span>
      </label>

      {error && (
        <p data-testid="contact-error" role="alert" aria-live="assertive" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <button
        data-testid="contact-submit"
        type="submit"
        disabled={status === "loading"}
        className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#10B981] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#059669] transition-all shadow-sm disabled:opacity-70"
      >
        {status === "loading" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> {tagalog ? "Nagpapadala..." : "Sending..."}
          </>
        ) : (
          <>
            {tagalog ? "Ipadala ang mensahe" : "Send message"}
            <Send size={16} className="transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
}
